import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { formatTime } from '../../utils/date/date';
import { getTimeWrapperClassList } from '../../utils/input/input';
import {
  checkTimeWhenDateChanged,
  timeInputFieldOnKeyDown,
  handleTimeFieldInput,
} from '../../utils/time/time';
import './Time.scss';
import { TimeErrorMessages } from '../../utils/time/time';
import SvgIcon from '../SvgIcon/SvgIcon';
import Text from '../Text/Text';

const Time = ({
  inputId,
  label,
  setTimeInput,
  datePickerUpdate,
  inactive = false,
  placeholder = '',
  tabIndex = 0,
  timeValueProp,
  bookingDate,
  setFormError,
  cannotUsePastTimeValueValidation = false,
  showClearButton,
  onTimeClear,
  defaultToNowOnInputClear,
  error,
  disabled = false,
}: {
  inputId: string;
  label: string;
  setTimeInput: () => void;
  datePickerUpdate?: boolean;
  bookingDate?: Date;
  inactive?: boolean;
  placeholder?: string;
  timeValueProp?: string;
  setFormError?: () => void;
  cannotUsePastTimeValueValidation?: boolean;
  showClearButton?: (v: unknown) => void;
  onTimeClear?: () => void;
  defaultToNowOnInputClear?: boolean;
  tabIndex?: number;
  error?: string;
  disabled?: boolean;
}) => {
  const [internalError, setInternalError] = useState('');
  const [localTime, setLocalTime] = useState<string | null | undefined>();

  useEffect(() => {
    if (timeValueProp && Date.parse(timeValueProp)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalTime(formatTime(timeValueProp));
    } else {
      setLocalTime(timeValueProp);
    }
  }, [timeValueProp]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (localTime?.length === 0) setInternalError(TimeErrorMessages.ResetError);

    checkTimeWhenDateChanged(
      formatTime(timeValueProp),
      internalError || error,
      setInternalError,
      setFormError,
      bookingDate,
      cannotUsePastTimeValueValidation,
      inactive,
    );

    //TODO: check if unreachable
    /* v8 ignore if -- @preserve */
    /* istanbul ignore if */
    if (localTime === '2400') setLocalTime(formatTime('0'));
  }, [
    localTime,
    bookingDate,
    timeValueProp,
    error,
    setFormError,
    cannotUsePastTimeValueValidation,
    inactive,
    internalError,
  ]);

  const handleTimeInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (inactive) return;

      handleTimeFieldInput(
        e,
        inputId,
        setLocalTime,
        setInternalError,
        setFormError,
        setTimeInput,
        cannotUsePastTimeValueValidation,
        bookingDate,
        inactive,
      );
    },
    [inactive, inputId, setFormError, setTimeInput, cannotUsePastTimeValueValidation, bookingDate],
  );

  return (
    <div className="time-container">
      {label && (
        <label htmlFor={inputId} className="body-small">
          <Text type="body" size="small">
            {label}
          </Text>
        </label>
      )}
      <div className={getTimeWrapperClassList('time-wrapper body', inactive, error)}>
        <input
          id={inputId}
          aria-invalid={!!(error || internalError)}
          aria-errormessage="time-error-msg"
          data-testid={inputId}
          type="text"
          readOnly={disabled}
          disabled={disabled}
          pattern="[0-9]{2}:[0-9]{2}"
          maxLength={5}
          minLength={5}
          placeholder={placeholder ? placeholder : 'HH : MM'}
          tabIndex={tabIndex}
          value={localTime || ''}
          onChange={handleTimeInput}
          onFocus={() => !timeValueProp && setLocalTime('')}
          onKeyDown={e =>
            bookingDate &&
            timeInputFieldOnKeyDown(
              e,
              inputId,
              localTime,
              setLocalTime,
              setTimeInput,
              datePickerUpdate,
              bookingDate,
            )
          }
        />
        {!disabled && showClearButton?.(localTime) ? (
          <div className="clear-button-container">
            <div
              className="clear-button"
              onClick={() => {
                setInternalError(TimeErrorMessages.ResetError);
                setLocalTime(defaultToNowOnInputClear ? 'Now' : null);
                if (onTimeClear) onTimeClear();
              }}
            >
              <SvgIcon iconName="Xmark" width={10} height={10} />
            </div>
          </div>
        ) : null}
      </div>
      {(error || internalError) && (
        <Text type="body" size="small" className="error-message">
          {internalError || error}
        </Text>
      )}
    </div>
  );
};

export default Time;
